import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fileUrl, api } from "@/lib/api";
import { flushQueue, getQueue } from "@/lib/offline";
import { toast } from "sonner";
import {
  LayoutDashboard, FileText, Users, Settings, LogOut, HardHat, Plus, ChevronLeft, User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Accueil", icon: LayoutDashboard, admin: false, end: true },
  { to: "/interventions", label: "Bons", icon: FileText, admin: false },
  { to: "/utilisateurs", label: "Users", icon: Users, admin: true },
  { to: "/parametres", label: "Réglages", icon: Settings, admin: true },
];

const PAGE_TITLES = [
  { match: (p) => p === "/", title: "Accueil" },
  { match: (p) => p === "/interventions", title: "Bons d'intervention" },
  { match: (p) => p === "/interventions/nouveau", title: "Nouveau bon" },
  { match: (p) => /^\/interventions\/[^/]+\/edition$/.test(p), title: "Modifier le bon" },
  { match: (p) => /^\/interventions\/[^/]+$/.test(p), title: "Détail du bon" },
  { match: (p) => p === "/utilisateurs", title: "Utilisateurs" },
  { match: (p) => p === "/parametres", title: "Paramètres" },
  { match: (p) => p === "/mon-compte", title: "Mon compte" },
];

export function AppLayout({ children }) {
  const { user, settings, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const items = NAV.filter((n) => !n.admin || isAdmin);

  useEffect(() => {
    const flush = async () => {
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      if (!getQueue().length) return;
      const { sent, failed } = await flushQueue(api);
      if (sent > 0) toast.success(`${sent} élément(s) hors ligne synchronisé(s) (bons, photos, envois)`);
      if (failed > 0) toast.error(`${failed} élément(s) hors ligne refusé(s) par le serveur (bon verrouillé ou invalide)`);
    };
    flush();
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, []);

  const path = location.pathname;
  const isDetailOrForm = /^\/interventions\/.+/.test(path);
  const isEditForm = path === "/interventions/nouveau" || /\/edition$/.test(path);
  const pageTitle = (PAGE_TITLES.find((t) => t.match(path)) || {}).title || "IR INGENIERIE";

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Brand = () => (
    <div className="flex items-center gap-2.5 px-5 py-5">
      {settings?.logo_path ? (
        <img src={fileUrl(settings.logo_path)} alt="logo" className="h-9 w-9 rounded-lg bg-white object-contain p-0.5" />
      ) : (
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500">
          <HardHat className="h-5 w-5 text-slate-900" />
        </div>
      )}
      <div className="leading-tight">
        <p className="font-head text-sm font-extrabold tracking-tight text-white">IR INGENIERIE</p>
        <p className="text-[11px] font-medium text-amber-400">{settings?.company_name || "Pro"}</p>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-[100dvh] bg-slate-50 dark:bg-slate-950">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col bg-slate-900 lg:flex">
        <Brand />
        <nav className="flex-1 space-y-1 px-3">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors ${
                  isActive ? "bg-amber-500 text-slate-900" : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`
              }>
              <n.icon className="h-[18px] w-[18px]" />
              {n.label === "Users" ? "Utilisateurs" : n.label === "Réglages" ? "Paramètres" : n.label === "Bons" ? "Bons d'intervention" : "Tableau de bord"}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <NavLink to="/mon-compte" data-testid="nav-mon-compte"
            className="mb-2 flex items-center gap-2.5 rounded-lg bg-white/5 px-3 py-2.5 transition-colors hover:bg-white/10">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-slate-900">
              {(user?.name || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-xs font-semibold text-white">{user?.name}</p>
              <p className="truncate text-[11px] text-amber-400">Mon compte</p>
            </div>
          </NavLink>
          <Button onClick={doLogout} variant="ghost" data-testid="logout-btn"
            className="w-full justify-start gap-2 text-slate-300 hover:bg-white/10 hover:text-white">
            <LogOut className="h-4 w-4" /> Déconnexion
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        {/* App-style top header */}
        <header className="pt-safe sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-900/90">
          <div className="flex h-14 items-center gap-3 px-4 lg:h-16 lg:px-8">
            {isDetailOrForm ? (
              <button onClick={() => navigate(-1)} data-testid="app-back-btn"
                className="-ml-1 flex h-9 w-9 items-center justify-center rounded-full text-slate-700 active:bg-slate-100 dark:text-slate-200 dark:active:bg-slate-800 lg:hidden">
                <ChevronLeft className="h-6 w-6" />
              </button>
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500 lg:hidden">
                <HardHat className="h-5 w-5 text-slate-900" />
              </div>
            )}
            <h1 className="font-head flex-1 truncate text-lg font-extrabold tracking-tight text-slate-900 dark:text-white">
              {pageTitle}
            </h1>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-account-btn"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 active:scale-95 dark:bg-slate-800 dark:text-slate-300 lg:hidden">
                  <User className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="truncate text-sm font-semibold">{user?.name}</p>
                  <p className="truncate text-xs font-normal text-amber-600 dark:text-amber-400">{isAdmin ? "Administrateur" : "Technicien"}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/mon-compte")} data-testid="menu-account-btn" className="gap-2">
                  <User className="h-4 w-4" /> Mon compte
                </DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout-btn" className="gap-2 text-red-600 focus:text-red-700">
                  <LogOut className="h-4 w-4" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="w-full min-w-0 flex-1 overflow-x-clip p-4 pb-28 sm:p-6 lg:p-8 lg:pb-8">{children}</main>
      </div>

      {/* Mobile bottom tab bar (app-like) — hidden on full-screen edit form */}
      {!isEditForm && (
        <nav className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 lg:hidden">
          <div className="mx-auto grid max-w-md" style={{ gridTemplateColumns: `repeat(${3 + (isAdmin ? items.length - 2 : 1)}, minmax(0, 1fr))` }}>
            {items.slice(0, 2).map((n) => (
              <TabItem key={n.to} to={n.to} end={n.end} icon={n.icon} label={n.label} testId={`tab-${n.to === "/" ? "home" : n.to.slice(1)}`} />
            ))}
            <button onClick={() => navigate("/interventions/nouveau")} data-testid="tab-new"
              className="flex flex-col items-center justify-center py-1.5">
              <span className="flex h-11 w-11 -translate-y-3 items-center justify-center rounded-full bg-amber-500 shadow-lg shadow-amber-500/30 active:scale-95">
                <Plus className="h-6 w-6 text-slate-900" />
              </span>
              <span className="-mt-2 text-[10px] font-semibold text-slate-500">Nouveau</span>
            </button>
            {isAdmin
              ? items.slice(2).map((n) => (
                  <TabItem key={n.to} to={n.to} end={n.end} icon={n.icon} label={n.label} testId={`tab-${n.to.slice(1)}`} />
                ))
              : <TabItem to="#logout" onClickOverride={doLogout} icon={LogOut} label="Sortir" testId="tab-logout" />}
          </div>
        </nav>
      )}
    </div>
  );
}

function TabItem({ to, end, icon: Icon, label, testId, onClickOverride }) {
  if (onClickOverride) {
    return (
      <button onClick={onClickOverride} data-testid={testId}
        className="flex flex-col items-center justify-center gap-0.5 py-2 text-slate-500 active:scale-95">
        <Icon className="h-[22px] w-[22px]" />
        <span className="text-[10px] font-semibold">{label}</span>
      </button>
    );
  }
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-0.5 py-2 transition-colors active:scale-95 ${
          isActive ? "text-amber-600 dark:text-amber-400" : "text-slate-500"
        }`
      }>
      {({ isActive }) => (
        <>
          <Icon className="h-[22px] w-[22px]" strokeWidth={isActive ? 2.6 : 2} />
          <span className="text-[10px] font-semibold">{label}</span>
        </>
      )}
    </NavLink>
  );
}
